import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { ErrorBoundary } from 'wsm-error-boundary';
import { ButtonList, ButtonListTwoColumn } from 'wsm-vehicle-cta-display';
import { InstantEpriceContainer } from 'wsm-pricing-display';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import { usePrefs, useFlags } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';

import { pricingMap } from '../../../utilities/pricingMap';
import vehiclePropType from '../../../propTypes/vehiclePropType';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../../../utilities/getTargetLocations';
import { getVehicleObj } from '../../../utilities/vehicleTracking';

const CTAButtons = ({
	accountId,
	isDisplayingEPrice,
	setIsDisplayingEPrice,
	vehicle
}) => {
	const {
		accountId: vehicleAccountId,
		uuid,
		inventoryButtons = [],
		pricing = {}
	} = vehicle;
	const userProfile = useSelector((state) => state.userProfile);
	const pageAlias = useSelector((state) => state.widgetInfo.pageAlias);

	const flags = useFlags();
	const {
		buttonSizeEPrice,
		buttonStyleEPrice,
		dealCentralRemoveCTAs,
		ePriceFontWeight,
		instantEpricePreventMultipleEpriceLeads,
		instantEpriceShowAllAfterLeadSubmit,
		restrictInsertLocationsForVehicles,
		showInstantEpriceForSharedInventory,
		allowTwoColumnCTAs
	} = usePrefs();

	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);
	const vehicleCtasLocation = getVehicleLocationName(
		'vehicle-ctas',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const componentPricing = pricingMap(pricing);
	const showEPrice = (id) => {
		setIsDisplayingEPrice(id === uuid && id !== 'none');
	};

	// On Deal Central SRP, do not display inventory buttons (editor-configured CTAs) but allow WIAPI CTAs to be inserted
	const inventoryButtonsToDisplay =
		dealCentralRemoveCTAs === 'true' ? [] : inventoryButtons;
	const hasInventoryButtons = Boolean(
		inventoryButtonsToDisplay && inventoryButtonsToDisplay.length > 0
	);

	const vehicleTrackingData = getVehicleObj(uuid, vehicle.title);

	// Helper function to render the appropriate button list component
	const renderButtonList = (props) => {
		const ButtonComponent =
			allowTwoColumnCTAs === 'true' ? ButtonListTwoColumn : ButtonList;
		return <ButtonComponent {...props} />;
	};

	return (
		<>
			<ErrorBoundary
				errorHandler={(error, errorInfo) => {
					setNewRelicCustomAttribute(
						'SRP ERROR',
						`ws-inv-listing error boundary: cta buttons error.\n${error}`
					);
					const newError = new Error(
						`ws-inv-listing error boundary.\n${error}`
					);
					newError.originalError = error;
					newError.originalStackTrace = errorInfo.componentStack;
					logger.error(`${newError}\n${newError.originalStackTrace}`);
				}}
			>
				{targetLocations.has('vehicle-ctas') ? (
					renderButtonList({
						buttons: [],
						vehicleTrackingData: undefined,
						dataLocation: vehicleCtasLocation
					})
				) : (
					<>
						{!isDisplayingEPrice && (
							<InstantEpriceContainer
								accountId={accountId}
								buttonSize={buttonSizeEPrice}
								buttonStyle={buttonStyleEPrice}
								ePriceFontWeight={ePriceFontWeight}
								instantEpricePreventMultipleEpriceLeads={
									instantEpricePreventMultipleEpriceLeads
								}
								instantEpriceShowAllAfterLeadSubmit={
									instantEpriceShowAllAfterLeadSubmit
								}
								pageAlias={pageAlias}
								pricing={componentPricing}
								showEPrice={(id) => showEPrice(id)}
								showInstantEpriceForSharedInventory={
									showInstantEpriceForSharedInventory
								}
								userProfile={userProfile}
								vehicleId={uuid}
								vehicleAccountId={vehicleAccountId}
								vehicleTrackingData={vehicleTrackingData}
							/>
						)}
						{renderButtonList({
							buttons: inventoryButtonsToDisplay,
							isSRP: true,
							useSRPWindowStickerPopover:
								flags['srp-window-sticker-link-change'],
							vehicleTrackingData: hasInventoryButtons
								? vehicleTrackingData
								: undefined,
							dataLocation: vehicleCtasLocation
						})}
					</>
				)}
			</ErrorBoundary>
		</>
	);
};

CTAButtons.propTypes = {
	accountId: PropTypes.string,
	isDisplayingEPrice: PropTypes.bool.isRequired,
	setIsDisplayingEPrice: PropTypes.func.isRequired,
	vehicle: vehiclePropType.isRequired
};

export default CTAButtons;
